/**
 * @brief 独立 MQTT 控制台 /console
 * @note 不依赖 Redux，不进 RTC 房间；只跟 MQTT broker 打交道
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Message,
  Radio,
  Select,
  Slider,
  Switch,
  Typography,
} from '@arco-design/web-react';
import type { CSSProperties } from 'react';
import consoleClient, { ConnectState } from './consoleClient';
import {
  makeBroadcastText,
  makeInjectHistory,
  makeInterrupt,
  makeSetMode,
  makeSwitchLlm,
  makeSwitchVoice,
  makeUserText,
  AnswerMode,
  LlmProvider,
  CmdEnvelope,
} from '@/lib/commands';
import { MQTT_BROKER_URL } from '@/config';
import {
  fetchModels,
  loadProviderStore,
  saveProviderStore,
  DEFAULT_MODELS,
  type ProviderStore,
} from './llmProviders';
import styles from './index.module.less';

const { Title } = Typography;
const LS_KEY = 'rtc-aigc-console-mqtt-config-v2';

interface ConsoleConfig {
  brokerUrl: string;
  roomId: string;
}

interface LlmRuntime {
  provider: LlmProvider;
  model: string;
  baseURL: string;
  apiKey: string;
  systemPrompt: string;
}

interface LogEntry {
  ts: number;
  type: string;
  summary: string;
  ok: boolean;
  error?: string;
}

const PROVIDER_OPTIONS: { label: string; value: LlmProvider }[] = [
  { label: 'OpenAI', value: 'openai' },
  { label: 'Anthropic', value: 'anthropic' },
  { label: 'OpenRouter', value: 'openrouter' },
  { label: 'DeepSeek', value: 'deepseek' },
  { label: '豆包 Ark', value: 'doubao-ark' },
];

const VOICE_PRESETS = [
  { label: '新版女声（默认 v2）', value: 'zh_female_vv_uranus_bigtts' },
  { label: '男声（通用）', value: 'zh_male_M392_conversation_wvae_bigtts' },
  { label: '旧版女声（v1）', value: 'BV001_streaming' },
];

const readSavedConfig = () => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const readSavedLlm = (): LlmRuntime => {
  const saved = readSavedConfig()?.llm;
  if (!saved || typeof saved !== 'object') {
    return {
      provider: 'openai',
      model: 'gpt-4o-mini',
      baseURL: '',
      apiKey: '',
      systemPrompt: '',
    };
  }
  return {
    provider:
      PROVIDER_OPTIONS.some((p) => p.value === saved.provider) && saved.provider
        ? saved.provider
        : 'openai',
    model: typeof saved.model === 'string' ? saved.model : 'gpt-4o-mini',
    baseURL: typeof saved.baseURL === 'string' ? saved.baseURL : '',
    apiKey: typeof saved.apiKey === 'string' ? saved.apiKey : '',
    systemPrompt: typeof saved.systemPrompt === 'string' ? saved.systemPrompt : '',
  };
};

export default function ConsolePage() {
  const [cfg, setCfg] = useState<ConsoleConfig>(() => {
    const saved = readSavedConfig();
    if (saved && typeof saved.brokerUrl === 'string') {
      return {
        brokerUrl: saved.brokerUrl,
        roomId: typeof saved.roomId === 'string' ? saved.roomId : '',
      };
    }
    return { brokerUrl: MQTT_BROKER_URL, roomId: '' };
  });

  const [connState, setConnState] = useState<ConnectState>('idle');
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const [mode, setMode] = useState<AnswerMode>('doubao-llm');
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastSpeakNow, setBroadcastSpeakNow] = useState(false);
  const [userText, setUserText] = useState('');

  const [llm, setLlm] = useState<LlmRuntime>(readSavedLlm);
  const [providerStore, setProviderStore] = useState<ProviderStore>(() => loadProviderStore());
  const [modelOptions, setModelOptions] = useState<string[]>(() => {
    const store = loadProviderStore();
    const p = readSavedLlm().provider;
    return store[p]?.models ?? DEFAULT_MODELS[p] ?? [];
  });
  const [modelsLoading, setModelsLoading] = useState(false);

  const [voice, setVoice] = useState({
    voice_type: VOICE_PRESETS[0].value,
    speed_ratio: 1,
    pitch_ratio: 1,
    volume_ratio: 1,
  });

  const [injectRole, setInjectRole] = useState<'user' | 'assistant'>('assistant');
  const [injectText, setInjectText] = useState('');

  const [stateMonitor, setStateMonitor] = useState<string>('');

  useEffect(() => {
    const off = consoleClient.onState(setConnState);
    const offState = consoleClient.onStateTopic((raw) => setStateMonitor(raw));
    return () => {
      off();
      offState();
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ ...cfg, llm }));
    } catch {
      /* noop */
    }
  }, [cfg, llm]);

  const handleProviderChange = (p: LlmProvider) => {
    const saved = providerStore[p];
    const newLlm: LlmRuntime = {
      ...llm,
      provider: p,
      apiKey: saved?.apiKey ?? '',
      baseURL: saved?.baseURL ?? '',
      model: '',
    };
    setLlm(newLlm);
    setModelOptions(saved?.models ?? DEFAULT_MODELS[p] ?? []);
  };

  const handleRefreshModels = async () => {
    if (!llm.apiKey) {
      Message.error('请先填写 API Key 再刷新模型');
      return;
    }
    setModelsLoading(true);
    try {
      const models = await fetchModels(llm.provider, llm.apiKey, llm.baseURL || undefined);
      setModelOptions(models);
      const updated: ProviderStore = {
        ...providerStore,
        [llm.provider]: {
          ...providerStore[llm.provider],
          apiKey: llm.apiKey,
          baseURL: llm.baseURL || undefined,
          models,
        },
      };
      setProviderStore(updated);
      saveProviderStore(updated);
      Message.success(`已刷新 ${models.length} 个模型`);
    } catch (e) {
      Message.error(`刷新失败: ${(e as Error).message}`);
    } finally {
      setModelsLoading(false);
    }
  };

  const handleLlmFieldChange = (fields: Partial<LlmRuntime>) => {
    const updated = { ...llm, ...fields };
    setLlm(updated);
    const pStore = providerStore[updated.provider] ?? {};
    setProviderStore({
      ...providerStore,
      [updated.provider]: {
        ...pStore,
        ...(fields.apiKey !== undefined ? { apiKey: fields.apiKey } : {}),
        ...(fields.baseURL !== undefined ? { baseURL: fields.baseURL } : {}),
      },
    });
  };

  useEffect(() => {
    saveProviderStore(providerStore);
  }, [providerStore]);

  const addLog = (entry: LogEntry) => {
    setLogs((prev) => [entry, ...prev].slice(0, 50));
  };

  const handleConnect = () => {
    if (!cfg.brokerUrl || !cfg.roomId) {
      Message.error('Broker URL 和 Room ID 都必填');
      return;
    }
    consoleClient.connect(cfg);
  };

  const handleDisconnect = () => consoleClient.disconnect();

  const publish = async (cmd: CmdEnvelope, summary: string) => {
    try {
      await consoleClient.publishCmd(cfg.roomId, cmd);
      addLog({ ts: Date.now(), type: cmd.type, summary, ok: true });
      Message.success(`${cmd.type} 已发送`);
    } catch (e) {
      const msg = (e as Error).message;
      addLog({ ts: Date.now(), type: cmd.type, summary, ok: false, error: msg });
      Message.error(`发送失败: ${msg}`);
    }
  };

  const canSend = connState === 'connected' && !!cfg.roomId;

  const stateClass = useMemo<CSSProperties>(() => ({ marginRight: 4 }), []);

  return (
    <div className={styles.consolePage}>
      <div className={styles.header}>
        <Title heading={5} style={{ margin: 0 }}>
          MQTT 控制台
        </Title>
        <span className={styles.hint}>
          下发目标：<code>rtc-aigc/&lt;roomId&gt;/cmd</code>
        </span>
      </div>

      <div className={styles.grid}>
        {/* 连接卡片 */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>
            <span style={stateClass} className={`${styles.statusDot} ${styles[connState]}`} />
            连接（{connState}）
          </h3>
          <div className={styles.row}>
            <Input
              style={{ width: 340 }}
              placeholder="wss://mqtt.teeyouth.cn/mqtt"
              value={cfg.brokerUrl}
              onChange={(v) => setCfg({ ...cfg, brokerUrl: v })}
            />
          </div>
          <div className={styles.row}>
            <Input
              style={{ width: 240 }}
              placeholder="目标 Room ID"
              value={cfg.roomId}
              onChange={(v) => setCfg({ ...cfg, roomId: v })}
            />
          </div>
          <div className={styles.row}>
            <span style={{ color: '#86909c', fontSize: 12 }}>
              broker 凭证来自前端环境变量 REACT_APP_MQTT_USERNAME / PASSWORD
            </span>
          </div>
          <div className={styles.row}>
            <Button
              type="primary"
              onClick={handleConnect}
              disabled={connState === 'connected' || connState === 'connecting'}
            >
              连接
            </Button>
            <Button onClick={handleDisconnect} disabled={connState === 'idle'}>
              断开
            </Button>
          </div>
        </div>

        {/* set_mode */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>回答模式（set_mode）</h3>
          <Radio.Group value={mode} onChange={setMode}>
            <Radio value="doubao-llm">豆包 LLM（默认）</Radio>
            <Radio value="external-llm">外部 LLM</Radio>
            <Radio value="text-broadcast">文字播报</Radio>
          </Radio.Group>
          <div className={styles.row} style={{ marginTop: 12 }}>
            <Button
              type="primary"
              disabled={!canSend}
              onClick={() => publish(makeSetMode(mode), `mode=${mode}`)}
            >
              发送
            </Button>
          </div>
        </div>

        {/* broadcast_text */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>文字播报（broadcast_text）</h3>
          <Input.TextArea
            rows={3}
            placeholder="要让 Agent 说的文字"
            value={broadcastText}
            onChange={setBroadcastText}
          />
          <div className={styles.row} style={{ marginTop: 8 }}>
            <Switch checked={broadcastSpeakNow} onChange={setBroadcastSpeakNow} />
            <span>立刻打断并播（speakNow）</span>
          </div>
          <div className={styles.row}>
            <Button
              type="primary"
              disabled={!canSend || !broadcastText.trim()}
              onClick={() =>
                publish(
                  makeBroadcastText(broadcastText, broadcastSpeakNow),
                  broadcastText.slice(0, 30)
                )
              }
            >
              发送
            </Button>
          </div>
        </div>

        {/* switch_llm */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>发送用户文字（user_text）</h3>
          <Input.TextArea
            rows={3}
            placeholder="模拟用户输入，让当前回答模式处理"
            value={userText}
            onChange={setUserText}
          />
          <div className={styles.row}>
            <Button
              type="primary"
              disabled={!canSend || !userText.trim()}
              onClick={() => publish(makeUserText(userText), userText.slice(0, 30))}
            >
              发送
            </Button>
          </div>
        </div>

        {/* switch_llm */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>切换 LLM（switch_llm）</h3>
          <div className={styles.row}>
            <Select
              style={{ width: 160 }}
              value={llm.provider}
              onChange={(v) => handleProviderChange(v as LlmProvider)}
              options={PROVIDER_OPTIONS}
            />
            <Select
              style={{ flex: 1, minWidth: 160 }}
              showSearch
              placeholder="选择或输入 model"
              value={llm.model}
              onChange={(v) =>
                handleLlmFieldChange({ model: typeof v === 'string' ? v : String(v) })
              }
              options={modelOptions.map((m) => ({ label: m, value: m }))}
              allowCreate
              filterOption={(input, option) =>
                String((option as { label?: string; value?: string }).value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
            <Button loading={modelsLoading} onClick={handleRefreshModels} size="small">
              刷新模型
            </Button>
          </div>
          <div className={styles.row}>
            <Input
              style={{ width: 360 }}
              placeholder="baseURL（可选，空则用 provider 默认）"
              value={llm.baseURL}
              onChange={(v) => handleLlmFieldChange({ baseURL: v })}
            />
          </div>
          <div className={styles.row}>
            <Input
              style={{ width: 360 }}
              type="password"
              placeholder="API Key（控制台优先，Server .env 作为 fallback）"
              value={llm.apiKey}
              onChange={(v) => handleLlmFieldChange({ apiKey: v })}
            />
          </div>
          <Input.TextArea
            rows={2}
            placeholder="systemPrompt（可选）"
            value={llm.systemPrompt}
            onChange={(v) => handleLlmFieldChange({ systemPrompt: v })}
          />
          <div className={styles.row} style={{ marginTop: 8 }}>
            <Button
              type="primary"
              disabled={!canSend || !llm.model}
              onClick={() =>
                publish(
                  makeSwitchLlm({
                    provider: llm.provider,
                    model: llm.model,
                    baseURL: llm.baseURL || undefined,
                    apiKey: llm.apiKey || undefined,
                    systemPrompt: llm.systemPrompt || undefined,
                  }),
                  `${llm.provider}/${llm.model}`
                )
              }
            >
              发送
            </Button>
          </div>
        </div>

        {/* switch_voice */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>切换音色（switch_voice）</h3>
          <div className={styles.row}>
            <Select
              style={{ width: 260 }}
              value={voice.voice_type}
              onChange={(v) => setVoice({ ...voice, voice_type: v })}
              options={VOICE_PRESETS}
              allowCreate
            />
          </div>
          <div className={styles.row}>
            <span style={{ width: 70 }}>速度</span>
            <Slider
              style={{ flex: 1 }}
              min={0.2}
              max={2}
              step={0.1}
              value={voice.speed_ratio}
              onChange={(v) => setVoice({ ...voice, speed_ratio: v as number })}
            />
            <span style={{ width: 40 }}>{voice.speed_ratio.toFixed(1)}</span>
          </div>
          <div className={styles.row}>
            <span style={{ width: 70 }}>音调</span>
            <Slider
              style={{ flex: 1 }}
              min={0.2}
              max={2}
              step={0.1}
              value={voice.pitch_ratio}
              onChange={(v) => setVoice({ ...voice, pitch_ratio: v as number })}
            />
            <span style={{ width: 40 }}>{voice.pitch_ratio.toFixed(1)}</span>
          </div>
          <div className={styles.row}>
            <span style={{ width: 70 }}>音量</span>
            <Slider
              style={{ flex: 1 }}
              min={0.2}
              max={2}
              step={0.1}
              value={voice.volume_ratio}
              onChange={(v) => setVoice({ ...voice, volume_ratio: v as number })}
            />
            <span style={{ width: 40 }}>{voice.volume_ratio.toFixed(1)}</span>
          </div>
          <div className={styles.row}>
            <Button
              type="primary"
              disabled={!canSend}
              onClick={() => publish(makeSwitchVoice(voice), voice.voice_type)}
            >
              发送
            </Button>
          </div>
        </div>

        {/* interrupt + inject_history */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>其它指令</h3>
          <div className={styles.row}>
            <Button
              status="danger"
              disabled={!canSend}
              onClick={() => publish(makeInterrupt(), 'interrupt')}
            >
              打断（interrupt）
            </Button>
          </div>
          <div className={styles.row} style={{ marginTop: 16 }}>
            <Radio.Group value={injectRole} onChange={setInjectRole}>
              <Radio value="user">user</Radio>
              <Radio value="assistant">assistant</Radio>
            </Radio.Group>
          </div>
          <Input.TextArea
            rows={2}
            placeholder="inject_history 注入的文本"
            value={injectText}
            onChange={setInjectText}
          />
          <div className={styles.row} style={{ marginTop: 8 }}>
            <Button
              disabled={!canSend || !injectText.trim()}
              onClick={() =>
                publish(
                  makeInjectHistory(injectRole, injectText),
                  `${injectRole}: ${injectText.slice(0, 20)}`
                )
              }
            >
              注入历史
            </Button>
          </div>
        </div>

        {/* state monitor */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>客户端上报（state topic）</h3>
          <div className={styles.log} style={{ maxHeight: 120 }}>
            {stateMonitor || '（尚未收到上报）'}
          </div>
        </div>

        {/* sent log */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>最近发送（sentLog）</h3>
          <div className={styles.log}>
            {logs.length === 0
              ? '（暂无）'
              : logs.map((l) => (
                  <div key={l.ts} className={styles.logItem}>
                    <span className={styles.logTs}>{new Date(l.ts).toLocaleTimeString()}</span>
                    <span className={l.ok ? styles.logOk : styles.logErr}>[{l.type}]</span>{' '}
                    {l.summary}
                    {l.error ? <span className={styles.logErr}> — {l.error}</span> : null}
                  </div>
                ))}
          </div>
        </div>
      </div>
    </div>
  );
}
